import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { isStaleWrite } from "@/lib/staleWrite";

import {
  Item,
  HydratedItem,
  CreateItemParams,
  UpdateItemParams,
  ListItemsResponseSchema,
  GetItemResponseSchema,
  GetHydratedItemResponseSchema,
  CreateItemResponseSchema,
  UpdateItemResponseSchema,
} from "@forgepoint/shared";

interface ItemLoadingState {
  // A refetch of an already-cached item leaves this false, so views keep
  // rendering the cached copy instead of flashing a loading state.
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  // A failed refetch of an already-cached item lands here, not on getError,
  // so views keep rendering the cached copy instead of an error state.
  revalidateError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface ItemsStore {
  items: Map<string, Item>;
  hydratedItems: Map<string, HydratedItem>;
  itemLoadingStates: Map<string, ItemLoadingState>;
  listLoading: boolean;
  listError: string | null;

  getItem: (id: string) => Item | undefined;
  getHydratedItem: (id: string) => HydratedItem | undefined;
  getItemLoadingState: (id: string) => ItemLoadingState;
  fetchItems: (params?: {
    search?: string;
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) => Promise<{ ids: string[]; total: number }>;
  fetchItem: (id: string, nested?: boolean) => Promise<boolean>;
  createItem: (
    data: CreateItemParams,
  ) => Promise<{ item: Item | null; error: string | null }>;
  updateItem: (
    id: string,
    data: UpdateItemParams,
  ) => Promise<{ item: Item | null; error: string | null }>;
  deleteItem: (id: string) => Promise<{ success: boolean; error?: string }>;
  markItemsReviewed: (itemIds: string[]) => Promise<boolean>;
  mergeItems: (items: HydratedItem[]) => void;
}

const getInitialLoadingState = (): ItemLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  revalidateError: null,
  saveError: null,
  deleteError: null,
});

/**
 * Whether a fetch of this item is a background revalidation: the consumers of
 * this fetch already hold a copy they can render. Nested consumers (the item
 * editor) render exclusively from hydratedItems.
 */
const isRevalidation = (
  state: Pick<ItemsStore, "items" | "hydratedItems">,
  id: string,
  nested?: boolean,
): boolean =>
  nested
    ? state.hydratedItems.has(id)
    : state.items.has(id) || state.hydratedItems.has(id);

/**
 * Whether a fetched tree holds a version the hydrated cache does not have yet.
 * Rewriting unchanged items re-signs their asset urls and reloads every
 * drawing on screen, so a poll that returns only cached versions writes nothing.
 */
const carriesNewVersion = (
  hydratedItems: Map<string, HydratedItem>,
  item: HydratedItem,
): boolean =>
  [item, ...collectChildItems(item)].some((treeItem) => {
    const cachedItem = hydratedItems.get(treeItem.id);
    return (
      !cachedItem ||
      treeItem.updatedAt.getTime() > cachedItem.updatedAt.getTime()
    );
  });

export const useItemsStore = create<ItemsStore>((set, get) => ({
  items: new Map(),
  hydratedItems: new Map(),
  itemLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  getItem: (id: string) => get().items.get(id),

  getHydratedItem: (id: string) => get().hydratedItems.get(id),

  getItemLoadingState: (id: string) =>
    get().itemLoadingStates.get(id) || getInitialLoadingState(),

  fetchItems: async (params?: {
    search?: string;
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    set(() => ({ listLoading: true, listError: null }));

    try {
      const searchParams = new URLSearchParams();
      if (params?.search) {
        searchParams.set("search", params.search);
      }
      if (params?.includeArchived) {
        searchParams.set("includeArchived", "true");
      }
      if (params?.limit !== undefined) {
        searchParams.set("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        searchParams.set("offset", String(params.offset));
      }
      const queryString = searchParams.toString();
      const url = queryString ? `items?${queryString}` : "items";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListItemsResponseSchema.parse(response.data);
        set((state) => {
          const itemsMap = new Map(state.items);
          data.items.forEach((item) => {
            if (!isStaleWrite(itemsMap.get(item.id), item)) {
              itemsMap.set(item.id, item);
            }
          });
          return { items: itemsMap, listLoading: false };
        });
        return {
          ids: data.items.map((item) => item.id),
          total: data.total,
        };
      }

      set(() => ({ listError: "Failed to fetch items", listLoading: false }));
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set(() => ({ listError: "Failed to fetch items", listLoading: false }));
      return { ids: [], total: 0 };
    }
  },

  fetchItem: async (id: string, nested?: boolean) => {
    const recordFetchFailure = () => {
      set((state) => {
        const loadingStates = new Map(state.itemLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          ...(isRevalidation(state, id, nested)
            ? { revalidateError: "Failed to fetch item" }
            : { getError: "Failed to fetch item" }),
        });
        return { itemLoadingStates: loadingStates };
      });
    };

    set((state) => {
      const loadingStates = new Map(state.itemLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      const revalidating = isRevalidation(state, id, nested);
      loadingStates.set(id, {
        ...currentState,
        isLoadingGet: !revalidating,
        getError: null,
        revalidateError: null,
      });
      return { itemLoadingStates: loadingStates };
    });

    try {
      const url = nested ? `items/${id}?nested=true` : `items/${id}`;
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        if (nested) {
          const data = GetHydratedItemResponseSchema.parse(response.data);
          set((state) => {
            const loadingStates = new Map(state.itemLoadingStates);
            const currentState =
              loadingStates.get(id) || getInitialLoadingState();
            loadingStates.set(id, {
              ...currentState,
              isLoadingGet: false,
              getError: null,
              revalidateError: null,
            });
            if (!carriesNewVersion(state.hydratedItems, data.item)) {
              return { itemLoadingStates: loadingStates };
            }
            const itemsMap = new Map(state.items);
            const hydratedItemsMap = new Map(state.hydratedItems);
            // Guard both caches — consumers read whichever they need, and a
            // refresh that updated only one leaves the other serving stale data.
            if (!isStaleWrite(itemsMap.get(id), data.item)) {
              itemsMap.set(id, data.item);
            }
            if (!isStaleWrite(hydratedItemsMap.get(id), data.item)) {
              hydratedItemsMap.set(id, data.item);
            }
            // Nested items become their own entries too, so per-id consumers
            // and live refresh always converge on one copy per item.
            mergeItemsIntoMap(hydratedItemsMap, collectChildItems(data.item));
            return {
              items: itemsMap,
              hydratedItems: hydratedItemsMap,
              itemLoadingStates: loadingStates,
            };
          });
          return true;
        }
        const data = GetItemResponseSchema.parse(response.data);
        set((state) => {
          const itemsMap = new Map(state.items);
          const loadingStates = new Map(state.itemLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          if (!isStaleWrite(itemsMap.get(id), data.item)) {
            itemsMap.set(id, data.item);
          }
          loadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
            revalidateError: null,
          });
          return {
            items: itemsMap,
            itemLoadingStates: loadingStates,
          };
        });
        return true;
      }

      recordFetchFailure();
      return false;
    } catch (error) {
      captureException(error as Error);
      recordFetchFailure();
      return false;
    }
  },

  createItem: async (data: CreateItemParams) => {
    try {
      const response = await fetchApi("items", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreateItemResponseSchema.parse(response.data);
        set((state) => {
          const itemsMap = new Map(state.items);
          itemsMap.set(result.item.id, result.item);
          return { items: itemsMap };
        });
        return { item: result.item, error: null };
      }

      const errorMessage = response.error ?? "Failed to create item";
      return { item: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      return { item: null, error: "Failed to create item" };
    }
  },

  updateItem: async (id: string, data: UpdateItemParams) => {
    // The PUT response is un-hydrated; merging it would publish stale
    // hydration under a fresh updatedAt, so these saves refetch instead.
    const shouldRefetchHydrated =
      typeof data.fileAssetIds !== "undefined" ||
      typeof data.primaryFileAssetId !== "undefined" ||
      typeof data.organizationFileIds !== "undefined" ||
      typeof data.operations !== "undefined" ||
      typeof data.components !== "undefined" ||
      typeof data.upgrades !== "undefined" ||
      typeof data.yield !== "undefined" ||
      typeof data.pricingLines !== "undefined";

    set((state) => {
      const loadingStates = new Map(state.itemLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { itemLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`items/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdateItemResponseSchema.parse(response.data);
        set((state) => {
          const itemsMap = new Map(state.items);
          const hydratedItemsMap = new Map(state.hydratedItems);
          const loadingStates = new Map(state.itemLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();

          if (!isStaleWrite(itemsMap.get(id), result.item)) {
            itemsMap.set(id, result.item);
          }
          if (!shouldRefetchHydrated && hydratedItemsMap.has(id)) {
            const existingHydratedItem = hydratedItemsMap.get(id);
            if (
              existingHydratedItem &&
              !isStaleWrite(existingHydratedItem, result.item)
            ) {
              hydratedItemsMap.set(id, {
                ...existingHydratedItem,
                ...result.item,
                operations: existingHydratedItem.operations,
                components: existingHydratedItem.components,
                upgrades: existingHydratedItem.upgrades,
                primaryFileAsset: existingHydratedItem.primaryFileAsset,
                fileAssets: existingHydratedItem.fileAssets,
              });
            }
          }

          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return {
            items: itemsMap,
            hydratedItems: hydratedItemsMap,
            itemLoadingStates: loadingStates,
          };
        });
        if (shouldRefetchHydrated) {
          await get().fetchItem(id, true);
        }
        return { item: result.item, error: null };
      }

      const errorMessage = response.error ?? "Failed to update item";
      set((state) => {
        const loadingStates = new Map(state.itemLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { itemLoadingStates: loadingStates };
      });
      return { item: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.itemLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update item",
        });
        return { itemLoadingStates: loadingStates };
      });
      return { item: null, error: "Failed to update item" };
    }
  },

  deleteItem: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.itemLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { itemLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`items/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const itemsMap = new Map(state.items);
          const hydratedItemsMap = new Map(state.hydratedItems);
          const loadingStates = new Map(state.itemLoadingStates);
          itemsMap.delete(id);
          hydratedItemsMap.delete(id);
          loadingStates.delete(id);
          return {
            items: itemsMap,
            hydratedItems: hydratedItemsMap,
            itemLoadingStates: loadingStates,
          };
        });
        return { success: true };
      }

      const errorMessage =
        (response.data as { error?: string })?.error || "Failed to delete item";
      set((state) => {
        const loadingStates = new Map(state.itemLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { itemLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      const errorMessage = "Failed to delete item";
      set((state) => {
        const loadingStates = new Map(state.itemLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { itemLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    }
  },

  markItemsReviewed: async (itemIds: string[]) => {
    // Snapshot only the entries the optimistic update touches; the rollback
    // also skips any entry a newer refetch replaced while the request flew.
    const idsToReview = new Set(itemIds);
    const prevItems = new Map<string, Item>();
    for (const id of itemIds) {
      const item = get().items.get(id);
      if (item) {
        prevItems.set(id, item);
      }
    }
    const prevHydratedItems = new Map<string, HydratedItem>();
    for (const [key, hydrated] of get().hydratedItems) {
      if (updateReviewStatusDeep(hydrated, idsToReview) !== hydrated) {
        prevHydratedItems.set(key, hydrated);
      }
    }
    const rollback = () =>
      set((state) => {
        const itemsMap = new Map(state.items);
        const hydratedItemsMap = new Map(state.hydratedItems);
        prevItems.forEach((item, id) => {
          if (!isStaleWrite(itemsMap.get(id), item)) {
            itemsMap.set(id, item);
          }
        });
        prevHydratedItems.forEach((hydrated, id) => {
          if (!isStaleWrite(hydratedItemsMap.get(id), hydrated)) {
            hydratedItemsMap.set(id, hydrated);
          }
        });
        return { items: itemsMap, hydratedItems: hydratedItemsMap };
      });

    // Optimistic update — immediately mark as REVIEWED
    set((state) => {
      const itemsMap = new Map(state.items);
      const hydratedItemsMap = new Map(state.hydratedItems);
      for (const id of itemIds) {
        const item = itemsMap.get(id);
        if (item && item.reviewStatus === "NEEDS_REVIEW") {
          itemsMap.set(id, { ...item, reviewStatus: "REVIEWED" });
        }
      }
      // Deep-update nested components inside hydrated items
      for (const [key, hydrated] of hydratedItemsMap) {
        const updated = updateReviewStatusDeep(hydrated, idsToReview);
        if (updated !== hydrated) {
          hydratedItemsMap.set(key, updated);
        }
      }
      return { items: itemsMap, hydratedItems: hydratedItemsMap };
    });

    try {
      const response = await fetchApi("items/mark-reviewed", "POST", {
        body: { itemIds },
      });
      if (!response.ok) {
        rollback();
        return false;
      }
      return true;
    } catch (error) {
      captureException(error as Error);
      rollback();
      return false;
    }
  },

  mergeItems: (itemsToMerge: HydratedItem[]) => {
    set((state) => {
      const hydratedItemsMap = new Map(state.hydratedItems);
      mergeItemsIntoMap(hydratedItemsMap, collectItemTrees(itemsToMerge));
      return { hydratedItems: hydratedItemsMap };
    });
  },
}));

/**
 * Newest known updatedAt (ms) for an item across both caches; 0 when
 * uncached. Cost displays key their refetches on this, so any writer that
 * bumps the entity refreshes prices.
 */
export function useItemVersionMs(itemId: string | null | undefined): number {
  return useItemsStore((state) =>
    !itemId
      ? 0
      : Math.max(
          state.items.get(itemId)?.updatedAt.getTime() ?? 0,
          state.hydratedItems.get(itemId)?.updatedAt.getTime() ?? 0,
        ),
  );
}

/**
 * Reports whether a component item is controlled by an integration. Only the
 * parent's hydrated tree carries a component's integration mappings.
 */
export function useComponentItemSynced(
  parentItemId: string | undefined,
  itemId: string | undefined,
): boolean {
  return useItemsStore((state) => {
    if (!parentItemId || !itemId) {
      return false;
    }
    const entry = state.hydratedItems
      .get(parentItemId)
      ?.components.find((hydrated) => hydrated.itemId === itemId);
    return (entry?.component.externalMappings?.length ?? 0) > 0;
  });
}

/** Flattens item trees into every distinct item they contain, deduped by id. */
function collectItemTrees(items: HydratedItem[]): HydratedItem[] {
  const itemsById: Record<string, HydratedItem> = {};
  const walk = (nested: HydratedItem[]) => {
    nested.forEach((item) => {
      itemsById[item.id] = item;
      if (item.components) {
        walk(item.components.map((component) => component.component));
      }
      if (item.upgrades) {
        walk(item.upgrades.map((upgrade) => upgrade.upgrade));
      }
    });
  };
  walk(items);
  return Object.values(itemsById);
}

/** The items nested under an item's tree, excluding the item itself. */
function collectChildItems(item: HydratedItem): HydratedItem[] {
  return collectItemTrees([
    ...(item.components?.map((component) => component.component) ?? []),
    ...(item.upgrades?.map((upgrade) => upgrade.upgrade) ?? []),
  ]);
}

/** Merges items into the hydrated map in place, skipping stale copies. */
function mergeItemsIntoMap(
  hydratedItemsMap: Map<string, HydratedItem>,
  items: HydratedItem[],
): void {
  items.forEach((item) => {
    const existingItem = hydratedItemsMap.get(item.id);
    if (isStaleWrite(existingItem, item)) {
      return;
    }
    hydratedItemsMap.set(item.id, mergeHydratedItem(existingItem, item));
  });
}

function mergeHydratedItem(
  existingItem: HydratedItem | undefined,
  item: HydratedItem,
): HydratedItem {
  if (!existingItem) {
    return item;
  }

  const existingComponents = new Map(
    existingItem.components.map((component) => [
      component.component.id,
      component,
    ]),
  );
  const components = item.components.map((component) => {
    const existingComponent = existingComponents.get(component.component.id);
    if (!existingComponent) {
      return component;
    }
    return {
      ...existingComponent,
      ...component,
      component: mergeHydratedItem(
        existingComponent.component,
        component.component,
      ),
    };
  });

  const existingUpgrades = new Map(
    existingItem.upgrades.map((upgrade) => [upgrade.upgrade.id, upgrade]),
  );
  const upgrades = item.upgrades.map((upgrade) => {
    const existingUpgrade = existingUpgrades.get(upgrade.upgrade.id);
    if (!existingUpgrade) {
      return upgrade;
    }
    return {
      ...existingUpgrade,
      ...upgrade,
      upgrade: mergeHydratedItem(existingUpgrade.upgrade, upgrade.upgrade),
    };
  });

  return {
    ...existingItem,
    ...item,
    components,
    upgrades,
  };
}

/**
 * Recursively updates reviewStatus from NEEDS_REVIEW to REVIEWED
 * for items matching the given IDs, including nested components/upgrades.
 * Returns the same reference if nothing changed (avoids unnecessary re-renders).
 */
function updateReviewStatusDeep(
  item: HydratedItem,
  idsToReview: Set<string>,
): HydratedItem {
  const statusChanged =
    idsToReview.has(item.id) && item.reviewStatus === "NEEDS_REVIEW";

  const updatedComponents = item.components?.map((comp) => {
    const updated = updateReviewStatusDeep(comp.component, idsToReview);
    return updated === comp.component ? comp : { ...comp, component: updated };
  });
  const componentsChanged =
    updatedComponents?.some((c, i) => c !== item.components?.[i]) ?? false;

  const updatedUpgrades = item.upgrades?.map((upg) => {
    const updated = updateReviewStatusDeep(upg.upgrade, idsToReview);
    return updated === upg.upgrade ? upg : { ...upg, upgrade: updated };
  });
  const upgradesChanged =
    updatedUpgrades?.some((u, i) => u !== item.upgrades?.[i]) ?? false;

  if (!statusChanged && !componentsChanged && !upgradesChanged) {
    return item;
  }

  return {
    ...item,
    reviewStatus: statusChanged ? "REVIEWED" : item.reviewStatus,
    components: componentsChanged ? updatedComponents! : item.components,
    upgrades: upgradesChanged ? updatedUpgrades! : item.upgrades,
  };
}
